import { NON_BILLING_TERRITORY_TAB_PERMISSIONS } from '&/features/settings/territories/constants/territory-tabs';

/**
 * The Billing area (SWOOP-430). A top-level menu and five pages, plus the per-territory Billing defaults
 * tab in Settings. Deliberately NOT a module surface (absent from `packages/hetivo-lib-types/src/modules.ts`):
 * the Billing menu, its pages and the tab are gated by permission alone, never by a tenant module.
 */
export const BILLING_PATH = '/billing';
export const BILLING_RUN_PATH = '/billing/run';
export const BILLING_INVOICES_PATH = '/billing/invoices';
export const BILLING_ADJUSTMENTS_PATH = '/billing/adjustments';
export const BILLING_RUNS_PATH = '/billing/runs';
export const BILLING_EMAILS_PATH = '/billing/emails';

/** Where `/billing` lands, and where the old Leasing › Billing address (`/leasing/billing`) now redirects. */
export const BILLING_DEFAULT_PATH = BILLING_INVOICES_PATH;

/** Seeing the Billing menu and its pages — held by the Billing Officer, the Tenant Admin and the Territory Admin. */
export const BILLING_LIST_PERMISSION = 'billing:list';

/** Who may open a territory's Billing defaults tab (SWOOP-430): Tenant Admin, Territory Admin and Billing Officer. */
export const BILLING_SETTINGS_LIST_PERMISSION = 'billing-settings:list';

/** Who may change a territory's Billing defaults. Without it the tab is read-only: disabled fields, no save, no add row. */
export const BILLING_SETTINGS_MANAGE_PERMISSION = 'billing-settings:manage';

/** The Billing tab of Territory Settings (SWOOP-430): per-territory invoicing defaults, placed before Leasing. */
export const BILLING_TERRITORY_SETTINGS_PATH = '/settings/territories/$territoryId/billing';

/**
 * Whether the user has general (non-billing) access to a territory's settings: the right to view and edit the
 * territory itself, held by a Tenant Admin (`territories:manage`) and a Territory Admin (`territories:edit`).
 * A Fleet Manager and an Account Manager hold `territories:edit` too, so they keep the General tab.
 *
 * `territories:list` alone is NOT general access: the Billing Officer holds it (to reach Settings › Territories)
 * yet has no business on the General tab — see {@link isBillingOnlyTerritoryViewer}.
 */
export function canAccessTerritoryGeneral(hasPermission: (permission: string) => boolean): boolean {
  return hasPermission('territories:manage') || hasPermission('territories:edit');
}

/**
 * True for a user whose ONLY reason to be in a territory's settings is Billing: they can open the Billing
 * defaults tab (`billing-settings:list`) but hold no permission that opens any OTHER territory tab — neither
 * general territory access ({@link canAccessTerritoryGeneral}) nor any of the standard tabs or Leasing
 * ({@link NON_BILLING_TERRITORY_TAB_PERMISSIONS}). Such a user sees only the Billing tab; every other tab is
 * hidden and its address redirects them to Billing.
 *
 * The `billing-settings:list` half keeps the redirect from looping — a user sent to Billing can always open it.
 * Checking the full tab-permission set (not just `territories:edit|manage`) means a STACKED role — e.g. a
 * Location Manager who is also Billing Officer — keeps the Locations and Users tabs instead of being forced off
 * them. The tab list is single-sourced with the tabs themselves (see `territory-tabs`).
 */
export function isBillingOnlyTerritoryViewer(hasPermission: (permission: string) => boolean): boolean {
  return (
    hasPermission(BILLING_SETTINGS_LIST_PERMISSION) &&
    !canAccessTerritoryGeneral(hasPermission) &&
    !NON_BILLING_TERRITORY_TAB_PERMISSIONS.some((permission) => hasPermission(permission))
  );
}
